import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';

export default function HistoryPage() {
  const [showGraph, setShowGraph] = useState(false);
  const [showInspection, setShowInspection] = useState(false);
  const [sensorData, setSensorData] = useState([]);
  const [range, setRange] = useState(30);
  const [errorMessage, setErrorMessage] = useState('');

  const getRecent23Days = (data) => {
    if (data.length === 0) return [];
    const lastTimestamp = new Date(data[data.length - 1].timestamp);
    const startDate = new Date(lastTimestamp);
    startDate.setDate(startDate.getDate() - 22);
    return data.filter(entry => new Date(entry.timestamp) >= startDate);
  };

  const formatThaiTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const day = date.getDate();
    const hour = String(date.getHours()).padStart(2, '0');
    const monthNames = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
    const month = monthNames[date.getMonth()];
    return `${day} ${month} ${hour}:00`;
  };

  const handleSaveTemperatureHumidity = async () => {
    setShowGraph(true);
    setErrorMessage('');
    try {
      const res = await fetch(`https://projecteggincubator.com/api/get_sensor_log.php?range=${range}`);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const json = await res.json();

      if (!Array.isArray(json)) throw new Error('API response is not an array');
      if (json.length === 0) {
        setErrorMessage('ไม่พบข้อมูลในช่วงเวลาที่เลือก');
        return;
      }

      const limited = getRecent23Days(json);
      const formatted = limited.map(item => ({
        timestamp: formatThaiTimestamp(item.timestamp),
        temperature: parseFloat(item.temperature),
        humidity: parseFloat(item.humidity)
      }));

      setSensorData(formatted);
    } catch (err) {
      console.error('Error fetching sensor logs:', err);
      setErrorMessage('เชื่อมต่อไม่สำเร็จ หรือข้อมูลไม่ถูกต้อง');
    }
  };

  const handleResetGraph = () => {
    if (window.confirm("คุณแน่ใจหรือไม่ว่าจะเริ่มรอบใหม่? ข้อมูลจะถูกจัดเก็บก่อนลบ.")) {
      fetch('https://projecteggincubator.com/api/reset_graph.php', {
        method: 'POST',
      })
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            alert("✅ เริ่มรอบใหม่เรียบร้อยแล้ว");
            setSensorData([]);
            setShowGraph(false);
          } else {
            alert("❌ ไม่สามารถเริ่มรอบใหม่ได้: " + (data.error || ''));
          }
        })
        .catch(err => {
          console.error('Reset error:', err);
          alert("❌ เกิดข้อผิดพลาดในการเชื่อมต่อ");
        });
    }
  };

  const handleSaveInspectionResult = () => {
    setShowInspection(true);
  };

  const inspectionData = [
    { id: 1, image: 'egg-dark.png', date: '01/05/67', time: '09:00:00', result: 'Positive' },
    { id: 2, image: 'egg-yellow.png', date: '01/05/67', time: '09:05:00', result: 'Negative' },
    { id: 3, image: 'egg-orange.png', date: '01/05/67', time: '09:10:00', result: 'Negative' },
    { id: 4, image: 'egg-orange.png', date: '01/05/67', time: '09:15:00', result: 'Negative' },
    { id: 5, image: 'egg-dark.png', date: '01/05/67', time: '09:20:00', result: 'Positive' },
  ];

  return (
    <div className="page History" style={{ backgroundColor: '#FEE191', height: '100vh', padding: 0 }}>
      <Header />
      <div className="page" style={{ textAlign: 'center', marginTop: '2rem' }}>
        <h2>หน้าสำหรับเก็บข้อมูล</h2>

        <div style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
          <button onClick={handleSaveTemperatureHumidity} style={buttonStyle}>
            🌡️ 💧 ประวัติอุณหภูมิและความชื้น
          </button>

          <button onClick={handleSaveInspectionResult} style={buttonStyle}>
            🔬 ผลการตรวจสอบเชื้อไข่ไก่
          </button>

          <button onClick={handleResetGraph} style={buttonStyle}>
            🔄 เริ่มรอบใหม่ (Reset กราฟ)
          </button>
        </div>
      </div>

      {showGraph && (
        <div style={modalStyle.overlay}>
          <div style={modalStyle.modal}>
            <h3>📈 กราฟอุณหภูมิและความชื้น</h3>
            {errorMessage && <p style={{ color: 'red', fontWeight: 'bold' }}>{errorMessage}</p>}

            {!errorMessage && sensorData.length > 0 && (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={sensorData} margin={{ top: 20, right: 30, left: 10, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    tick={{ fontSize: 11 }}
                    angle={-25}
                    interval="preserveStartEnd"
                    textAnchor="end"
                    height={70}
                    tickMargin={8}
                  />
                  <YAxis
                    label={{ value: '°C / %', angle: -90, position: 'insideLeft', offset: 10 }}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip />
                  <Legend verticalAlign="top" height={36} />
                  <Line
                    type="monotone"
                    dataKey="temperature"
                    stroke="#f57c00"
                    strokeWidth={1.5}
                    dot={false}
                    name="อุณหภูมิ (°C)"
                  />
                  <Line
                    type="monotone"
                    dataKey="humidity"
                    stroke="#039be5"
                    strokeWidth={1.5}
                    dot={false}
                    name="ความชื้น (%)"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}

            <button style={{ ...buttonStyle, marginTop: '1rem' }} onClick={() => setShowGraph(false)}>✖ ปิด</button>
          </div>
        </div>
      )}

      {showInspection && (
        <div style={modalStyle.overlay}>
          <div style={{ ...modalStyle.modal, maxWidth: '800px' }}>
            <h3>🔬 ผลการตรวจสอบเชื้อไข่</h3>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th>Photo</th>
                  <th>Number</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Types of Egg</th>
                </tr>
              </thead>
              <tbody>
                {inspectionData.map((egg, index) => (
                  <tr key={egg.id}>
                    <td><img src={`/images/${egg.image}`} alt="egg" width="40" /></td>
                    <td>{index + 1}</td>
                    <td>{egg.date}</td>
                    <td>{egg.time}</td>
                    <td style={{ color: egg.result === 'Positive' ? 'green' : 'red' }}>{egg.result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button style={{ ...buttonStyle, marginTop: '1rem' }} onClick={() => setShowInspection(false)}>✖ ปิด</button>
          </div>
        </div>
      )}
    </div>
  );
}

const buttonStyle = {
  padding: '1rem 2rem',
  fontSize: '18px',
  fontWeight: 'bold',
  color: 'black',
  backgroundColor: 'white',
  border: '3px solid black',
  borderRadius: '12px',
  cursor: 'pointer',
  width: '320px'
};

const modalStyle = {
  overlay: {
    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
    backgroundColor: 'rgba(0,0,0,0.4)',
    display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 999
  },
  modal: {
    background: 'white',
    borderRadius: '12px',
    padding: '2rem',
    minWidth: '600px',
    maxWidth: '90%',
    textAlign: 'center'
  }
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: '1rem'
};
